import {saveAs} from 'file-saver';
import {AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import {AsyncResult, Conflict, NotFound, ServerError, Unauthorized} from 'Core/models/Async';
import PromiseWithCancellation from 'Core/models/PromiseWithCancellation';
import * as E from 'fp-ts/lib/Either';
import {getOrElse} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {CANCEL} from 'redux-saga';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {getAccessToken} from 'Shared/utilities/Auth';
import axiosInstance from 'Shared/utilities/axiosInstance';

export const getAxiosConfig = (cancelToken?: CancelToken, isFile?: boolean): AxiosRequestConfig => ({
  cancelToken,
  responseType: isFile ? 'blob' : undefined,
  paramsSerializer: params => addFilterParameters(params).slice(1),
  headers: {
    Authorization: `Bearer ${pipe(
      getAccessToken(),
      getOrElse(() => '')
    )}`,
    'Cache-Control': 'no-store, no-cache, must-revalidate',
    Pragma: 'no-cache',
  },
});

export const getUrlFromUri = (uri: string) => `${process.env.REACT_APP_BASE_GATEWAY_API_URL}${uri}`;

export const handleResponse = <TResponse>(response: AxiosResponse<TResponse>): AsyncResult<TResponse> => {
  if (response) {
    return response.status === 200 || response.status === 201 || response.status === 202 || response.status === 204
      ? E.right(response.data)
      : [401, 403].includes(response.status)
      ? E.left(new Unauthorized())
      : response.status === 404
      ? E.left(new NotFound(response.data))
      : [409].includes(response.status)
      ? E.left(new Conflict())
      : E.left(new ServerError(response.data));
  } else {
    return E.left(new ServerError());
  }
};

export const handleImageResponse = <TResponse>(response: AxiosResponse<TResponse>) => {
  if (response) {
    return response.status === 200 || response.status === 201 || response.status === 202 || response.status === 204
      ? E.right(response.data)
      : [401, 403].includes(response.status)
      ? E.left(new Unauthorized())
      : response.status === 404
      ? E.left(new NotFound(response.data))
      : [409].includes(response.status)
      ? E.left(new Conflict())
      : E.left(new ServerError(response.data));
  } 
};

type CreatePromiseFunc<TReturn> = (cancelToken: CancelToken) => Promise<TReturn>;
export const withCancellation = <T>(createPromise: CreatePromiseFunc<T>) => {
  const source = axiosInstance.CancelToken.source();
  const promise = PromiseWithCancellation.resolve<T>(createPromise(source.token));
  promise[CANCEL] = () => source.cancel();

  return promise;
};

export const get = <TResponse>(uri: string, isFile?: boolean) =>
  withCancellation(cancelToken =>
    axiosInstance.get<TResponse>(getUrlFromUri(uri), getAxiosConfig(cancelToken, isFile)).then(handleResponse)
  );
export const post = <TResponse>(uri: string, payload?: any) => {
  return withCancellation(cancelToken =>
    axiosInstance.post<TResponse>(getUrlFromUri(uri), payload, getAxiosConfig(cancelToken)).then(handleResponse)
  );
};

export const downloadFile = async (uri: string, downloadName: string, successResponse?: string) => {
  const response = await get<string>(removePrefixFromLink(uri), true);

  if (E.isRight(response)) {
    return ((pdfResponse: string) => {
      const blob = new Blob([pdfResponse]);

      saveAs(blob, downloadName);

      return successResponse ? successResponse : 'The download was successful.';
    })(response.right);
  } else {
    return response.left;
  }
};

export const removePrefixFromLink = (uri: string) => uri.replace('api/v1/', '');

export const postMultipart = <TResponse>(uri: string, payload: Blob, cancelTokenInput?: CancelToken) => {
  const data = new FormData();
  data.append('file', payload);

  return withCancellation(cancelToken =>
    axiosInstance
      .post<TResponse>(getUrlFromUri(uri), data, {
        ...getAxiosConfig(cancelTokenInput ? cancelTokenInput : cancelToken),
        headers: {
          ...getAxiosConfig(cancelToken).headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(handleResponse)
  );
};

export const put = <TResponse>(uri: string, payload?: any) =>
  withCancellation(cancelToken =>
    axiosInstance.put<TResponse>(getUrlFromUri(uri), payload, getAxiosConfig(cancelToken)).then(handleResponse)
  );
export const remove = (uri: string) => axiosInstance.delete(getUrlFromUri(uri), getAxiosConfig()).then(handleResponse);
